import { faCalendarDay } from '@fortawesome/pro-light-svg-icons/faCalendarDay'
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock'

import { DatePicker, TimePicker, Select } from 'src/components'
import { useUser } from 'src/hook/useUser'
import i18n from 'src/i18n'
import { DatePickerEventDetail } from 'src/components/DatePicker'
import { useSchedulePostForm } from 'src/hook/useSchedulePostForm'
import { Language } from 'src/utils/constants'

import { TIMEZONES, INVALID_DATE } from './constants'
import { SchedulePostFormProps } from './types'
import { getPublishingDates } from './utils'
import { useMinTime } from './hooks/useMinTime'

const SchedulePostForm = ({ id }: SchedulePostFormProps) => {
  const { locale } = useUser()
  const { isSelectedOption, setFormData, formData } = useSchedulePostForm()
  const minTime = useMinTime()

  const handleChange = (name: string, value?: string) =>
    value ? setFormData((prev) => ({ ...prev, [name]: value })) : undefined

  const handleChangeDate = ({ detail }: CustomEvent<DatePickerEventDetail>) => {
    const date = detail?.dateFormatted

    if (!date || date === INVALID_DATE || date.split('/')?.length < 3) {
      return handleChange('scheduledPubDate', '')
    }

    handleChange('scheduledPubDate', date)
  }

  const handleChangeTime = ({ detail }: CustomEvent<{ value: string }>) => {
    const time = new Date(detail.value ?? '').toTimeString()?.split(' ')?.[0]

    if (!time || time === INVALID_DATE || time.split(':')?.length < 2) {
      return handleChange('scheduledPubTime', '')
    }

    handleChange('scheduledPubTime', time)
  }

  const handleChangeTimezone = ({ detail }: CustomEvent<{ value: string }>) => {
    handleChange('scheduledPubTimeZoneId', detail?.value)
  }

  return (
    <form id={id} className="_d-flex _flex-column">
      <div>
        <DatePicker
          icon={faCalendarDay}
          className="_mb-5"
          label={i18n.t('forms.schedule_posts.date_picker.label')}
          placeholder={i18n.t('forms.schedule_posts.date_picker.placeholder')}
          onChange={handleChangeDate}
          locale={locale}
          value={formData.scheduledPubDate}
          publishingDateRange={getPublishingDates()}
        />
      </div>

      <div className="_d-flex _flex-1">
        <div className="_mr-3 _flex-1">
          <TimePicker
            icon={faClock}
            label={i18n.t('forms.schedule_posts.time_input.label')}
            placeholder={i18n.t('forms.schedule_posts.time_input.placeholder')}
            onChange={handleChangeTime}
            time24hr={locale?.toLowerCase() === Language.EN ? false : true}
            time={formData.scheduledPubTime}
            minTime={minTime}
          />
        </div>

        <div className="_ml-3 _flex-1">
          <Select
            label={i18n.t('forms.schedule_posts.select.label')}
            placeholder={i18n.t('forms.schedule_posts.select.placeholder')}
            name="scheduledPubTimeZoneId"
            onChange={handleChangeTimezone}
          >
            {TIMEZONES.map((timezone, index) => (
              <Select.Option
                key={timezone.value + index}
                dataTest={`option-${timezone.value}`}
                value={timezone.value}
                isSelected={isSelectedOption(timezone.value)}
              >
                {timezone.label} ({i18n.t(timezone.display_name)})
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
    </form>
  )
}

export default SchedulePostForm
